/* eslint-disable yoda */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter
} from '@ps-design-system/components/Dialog';
import { Text } from '@ps-design-system/components/Text';
import { Alert, AlertDescription } from '@ps-design-system/components/Alert';
import { Input } from '@ps-design-system/components/Input';
import { Button } from '@ps-design-system/components/Button';
import { MediaCard } from '@ps-design-system/components/MediaCard';
import { Spinner } from '@ps-design-system/components/Spinner';

import { useUploaderPageData } from '../context/UploaderPageContext';

const UploaderAddBase = ({
  title,
  description,
  linkTitle,
  linkDescription,
  alert,
  onBack,
  onAdd,
  onDelete,
  onDone,
  onValidation,
  media,
  isAdding
}) => {
  const [linkValue, setLinkValue] = useState('');
  const [isValid, setIsValid] = useState('');
  const { isMobile, intl } = useUploaderPageData();

  useEffect(() => {
    const validatedLink = onValidation(linkValue) && linkValue;
    setIsValid(validatedLink);
  }, [linkValue, onValidation]);

  const handleInputChange = useCallback((e) => {
    setLinkValue(e.target.value);
  }, []);

  const handleAddClick = () => {
    onAdd(linkValue);
    setLinkValue('');
  };

  return (
    <>
      <DialogHeader
        headerAlignment="left"
        showCloseButton
      >
        <DialogTitle className="uploader-dialog-header">
          {title}
        </DialogTitle>
        <DialogDescription>
          {description}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="add-body">
          <div className="add-header">
            <Text typeface="body-2-m">{linkTitle}</Text>
            <div className="add-input">
              <Input
                className="add-field"
                fullWidth={true}
                placeholder={intl.formatMessage({
                  id: 'uploader.media_modal.link',
                  defaultMessage: 'Link'
                })}
                value={linkValue}
                errorMsg={!isValid && linkValue !== '' ? linkDescription : ''}
                onChange={handleInputChange}
                attributes={{ 'data-testing-id': 'add-link-field' }}
              />
              <Button className="add-button" onClick={handleAddClick} disabled={!isValid} attributes={{ 'data-testing-id': 'modal-add-link' }}>
                <FormattedMessage id="uploader.media_modal.add" defaultMessage="Add" />
              </Button>
            </div>
            <Text typeface="caption-2" className={!isValid && linkValue !== '' ? 'tw-text-red-800' : ''}>{linkDescription}</Text>
          </div>
          <div className="add-content">
            {
              media
                .map((item) => (
                  <MediaCard
                    key={item.id}
                    imageUrl={item.imageUrl}
                    altText={item.altText}
                    onClose={() => onDelete(item)}
                    type={item.type}
                  />
                ))
            }
            {isAdding && <div className='spinner'><Spinner size="large" appearance="black" /></div>}
          </div>
          <Alert isDismissable={false}>
            <AlertDescription className="add-alert-description">{alert}</AlertDescription>
          </Alert>
        </div>
      </DialogBody>
      <DialogFooter className="add-dialog-footer">
        <div className="add-footer">
          <div>
            <Button
              appearance={isMobile ? 'white' : ''}
              variant={isMobile ? 'outline-1' : 'underline'}
              fullWidth={isMobile}
              onClick={onBack}
              attributes={{ 'data-testing-id': 'upload-modal-back' }}
            >
              <FormattedMessage id="uploader.page.back" defaultMessage="Back"/>
            </Button>
          </div>
          <div className="add-footer-button">
            <Button fullWidth={isMobile} onClick={onDone} appearance={isMobile ? 'black' : 'purple'} attributes={{ 'data-testing-id': 'upload-modal-done' }}>
              <FormattedMessage id="uploader.media_modal.done" defaultMessage="Done" />
            </Button>
          </div>
        </div>
      </DialogFooter>
    </>
  );
};

UploaderAddBase.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  linkTitle: PropTypes.node,
  linkDescription: PropTypes.node,
  alert: PropTypes.node,
  onBack: PropTypes.func,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onDone: PropTypes.func,
  onValidation: PropTypes.func,
  media: PropTypes.array,
  isAdding: PropTypes.bool
};

UploaderAddBase.defaultProps = {
  title: null,
  description: null,
  linkTitle: null,
  linkDescription: null,
  alert: null,
  onBack: () => {},
  onAdd: () => {},
  onDelete: () => {},
  onDone: () => {},
  onValidation: () => {},
  media: [],
  isAdding: false
};

export default UploaderAddBase;
